import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { Text } from '../../../../common/text/Text';
import { ButtonCustom } from '../../../../common/button/button';
import { Lozenge } from '../../../../common/lozenge/Lozenge';
import { CapGauge } from '../components/capgauge';
import { saisineErrorLabel } from '../../../usecases/saisine.reducer';
import { flightRangeLabel } from '../../../domain/saisine';

const formatDate = (date) => (date ? format(new Date(date), 'dd/MM/yyyy') : '—');
const formatAmount = (amount) => `${(amount || 0).toLocaleString('fr-FR')} €`;

const roomRange = (room) => flightRangeLabel(
  room.flight_date_min ? new Date(room.flight_date_min) : null,
  room.flight_date_max ? new Date(room.flight_date_max) : null,
);

const ClaimLine = ({ claim }) => (
  <ClaimRow>
    <ClaimRef>
      <Link to={{ pathname: '/dossier', search: `?id=${claim._id}&from=saisines` }}>
        {claim.from_id}
      </Link>
      {claim.mainPassenger && <span>{` ${claim.mainPassenger}`}</span>}
      <span>{` · ${claim.airport_departure || '?'} → ${claim.airport_arrival || '?'}`}</span>
      {claim.paxComplete === false && <span>{' '}<Lozenge text="pax incomplets" red /></span>}
    </ClaimRef>
    <span>{formatDate(claim.flight_date)}</span>
    <span>{`${claim.nbPassengers} pax`}</span>
    <ClaimAmount>{formatAmount(claim.compensation_total)}</ClaimAmount>
  </ClaimRow>
);

const AutofillPreview = ({
  proposal, loading, apply, cancel,
}) => {
  const { newRooms = [], topUps = [], nonGroupable = [] } = proposal;
  const nbClaims = newRooms.reduce((sum, room) => sum + room.claims.length, 0)
    + topUps.reduce((sum, room) => sum + room.claims.length, 0);

  const newRoomPayload = (room) => room.claims.map((claim) => claim._id);
  const topUpPayload = (room) => ({
    saisineId: room.saisineId,
    claimIds: room.claims.map((claim) => claim._id),
  });

  const applyAll = () => {
    apply({
      newRooms: newRooms.map(newRoomPayload),
      topUps: topUps.map(topUpPayload),
    });
  };

  const applyOneNewRoom = (room) => {
    apply({ newRooms: [newRoomPayload(room)], topUps: [] });
  };

  const applyOneTopUp = (room) => {
    apply({ newRooms: [], topUps: [topUpPayload(room)] });
  };

  return (
    <div>
      <SummaryBar>
        <Text color="secondary" variant="body2">
          {`${newRooms.length} nouvelle(s) saisine(s) · ${topUps.length} room(s) complétée(s) · ${nbClaims} dossier(s) groupé(s) · ${nonGroupable.length} non groupable(s)`}
        </Text>
        <div>
          <ButtonCustom text="Fermer" onClick={cancel} disabled={loading} />
          <ButtonCustom
            text={`Tout créer (${newRooms.length + topUps.length})`}
            onClick={applyAll}
            disabled={loading || (!newRooms.length && !topUps.length)}
          />
        </div>
      </SummaryBar>
      <CardsGrid>
        {newRooms.map((room, index) => (
          <RoomCard key={`new-${index}`}>
            <RoomHeader>
              <strong>{room.jurisdiction}</strong>
              <span>{` · ${room.airline_name} · ${room.claims.length} dossiers · ${room.claims.reduce((sum, claim) => sum + claim.nbPassengers, 0)} pax`}</span>
              <Lozenge text="Nouvelle" blue />
            </RoomHeader>
            {roomRange(room) && (
              <Text color="secondary" variant="body2">{roomRange(room)}</Text>
            )}
            {room.claims.map((claim) => <ClaimLine key={claim._id} claim={claim} />)}
            <RoomFooter>
              <CapGauge total={room.total} />
              <ButtonCustom
                text="Créer cette saisine"
                onClick={() => applyOneNewRoom(room)}
                disabled={loading}
              />
            </RoomFooter>
          </RoomCard>
        ))}
        {topUps.map((room) => (
          <RoomCard key={`topup-${room.saisineId}`}>
            <RoomHeader>
              <Link to={{ pathname: '/saisine', search: `?id=${room.saisineId}&from=saisines` }}>
                <strong>{room.jurisdiction}</strong>
              </Link>
              <span>{` · ${room.airline_name} · room existante`}</span>
              <Lozenge text="Complétée" green />
            </RoomHeader>
            {roomRange(room) && (
              <Text color="secondary" variant="body2">{roomRange(room)}</Text>
            )}
            {room.claims.map((claim) => <ClaimLine key={claim._id} claim={claim} />)}
            <Text color="secondary" variant="body2">
              {`${formatAmount(room.previousTotal)} → ${formatAmount(room.newTotal)}`}
            </Text>
            <RoomFooter>
              <CapGauge total={room.newTotal} />
              <ButtonCustom
                text="Compléter cette room"
                onClick={() => applyOneTopUp(room)}
                disabled={loading}
              />
            </RoomFooter>
          </RoomCard>
        ))}
      </CardsGrid>
      {nonGroupable.length > 0 && (
        <NonGroupableCard>
          <Text color="secondary" variant="body1">{`Non groupables (${nonGroupable.length})`}</Text>
          {nonGroupable.map((claim) => (
            <ClaimRow key={claim._id}>
              <ClaimRef>
                <Link to={{ pathname: '/dossier', search: `?id=${claim._id}&from=saisines` }}>
                  {claim.from_id}
                </Link>
                {claim.mainPassenger && <span>{` ${claim.mainPassenger}`}</span>}
                <span>{` · ${claim.jurisdiction || 'juridiction ?'} · ${claim.airline_name || '?'} · ${formatDate(claim.flight_date)} · ${claim.nbPassengers} pax · ${formatAmount(claim.compensation_total)}`}</span>
              </ClaimRef>
              <Lozenge text={saisineErrorLabel(claim.reason)} yellow />
            </ClaimRow>
          ))}
        </NonGroupableCard>
      )}
    </div>
  );
};

const SummaryBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 10px 0;
`;

const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(380px, 1fr));
  gap: 12px;
`;

const RoomCard = styled.div`
  border: 1px solid #dfe1e6;
  border-radius: 4px;
  padding: 12px 15px;
`;

const RoomHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 8px;
  font-size: 14px;
`;

const RoomFooter = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 8px;
`;

const ClaimRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 4px 0;
  font-size: 13px;
  border-bottom: 1px solid #f4f5f7;
`;

const ClaimRef = styled.div`
  flex: 1;
  min-width: 0;
`;

const ClaimAmount = styled.span`
  font-weight: 500;
`;

const NonGroupableCard = styled.div`
  border: 1px solid #dfe1e6;
  border-radius: 4px;
  padding: 12px 15px;
  margin-top: 12px;
`;

export {
  AutofillPreview,
};
